import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { Button, Collapse, Tooltip } from '@material-ui/core';

import { useBrandVoicePopup } from 'src/services/popup';
import { ChevronIcon } from 'src/components/ChevronIcon';
import PlusIcon from 'src/assets/icons/Plus';
import { ITEM_TYPE } from 'src/views/NavigationSidebarPanel/const';
import { useDeleteBrandVoice } from 'src/services/popup/popups/useDeleteBrandVoice';
import { TruncatedTypography } from 'src/components/TruncatedTypography';
import { IconEditPencil } from 'src/assets/icons/IconEditPencil';
import IconDelete from 'src/assets/icons/Delete';
import { ContextMenu } from 'src/components/ContextMenu';
import { SidebarSubmenuItem } from '../SidebarSubmenuItem';
import { SidebarButton } from '../SidebarButton';
import { useStyles } from './useStyles';

export const SidebarSubmenu = ({
  className,
  item,
  activePath,
  onToggleMenu,
  onToggleCollapse,
  menuItemId,
  collapseItemId,
  dataStonlyId,
}) => {
  const styles = useStyles();
  const isMenuExpand = menuItemId === item.id;

  const openBrandVoicePopup = useBrandVoicePopup();
  const deleteBrandVoice = useDeleteBrandVoice();

  const handleMenuItemClick = useCallback(() => {
    onToggleMenu(item);
  }, [onToggleMenu, item]);

  const handleCollapseItemClick = useCallback(
    (innerItem) => {
      onToggleCollapse(innerItem);
    },
    [onToggleCollapse]
  );

  const handleCreateBrandVoiceClick = useCallback(
    (e) => {
      e.stopPropagation();
      openBrandVoicePopup();
    },
    [openBrandVoicePopup]
  );

  const handleEditBrandVoiceClick = useCallback(
    (id, name) => {
      openBrandVoicePopup({ id, name });
    },
    [openBrandVoicePopup]
  );

  const handleDeleteBrandVoiceClick = useCallback(
    (id, name) => {
      deleteBrandVoice(id, name);
    },
    [deleteBrandVoice]
  );

  return (
    <div className={cx(styles.root, className, { [styles.expanded]: isMenuExpand })}>
      <SidebarButton
        className={styles.button}
        item={item}
        onClick={handleMenuItemClick}
        data-stonly-id={dataStonlyId}
        endIcon={
          <div>
            {item.displayCreateButton ? (
              <Tooltip
                title="Create new Brand voice"
                arrow
                placement="top"
              >
                <Button
                  className={styles.createButton}
                  onClick={handleCreateBrandVoiceClick}
                >
                  <PlusIcon className={styles.createIcon} />
                </Button>
              </Tooltip>
            ) : undefined}
            <ChevronIcon
              className={styles.iconExpand}
              direction={isMenuExpand ? 'down' : 'right'}
            />
          </div>
        }
      />
      <Collapse
        className={styles.collapse}
        in={isMenuExpand}
      >
        <div className={cx(styles.items, { [styles.scrollable]: item.scrollable })}>
          {item.items?.map((menuItem) => {
            if (menuItem.disabled) {
              return null;
            }

            if (menuItem.type === ITEM_TYPE.collapse) {
              const contextMenuItems = [
                {
                  id: 'edit',
                  dataTestId: `editBrandVoice${menuItem.id}MenuItem`,
                  label: 'Change name',
                  onClick: () => handleEditBrandVoiceClick(menuItem.brandVoiceId, menuItem.label),
                  icon: <IconEditPencil className={styles.iconContextMenu} />,
                },

                {
                  id: 'delete',
                  dataTestId: `deleteBrandVoice${menuItem.id}MenuItem`,
                  label: 'Delete',
                  onClick: () => handleDeleteBrandVoiceClick(menuItem.brandVoiceId, menuItem.label),
                  icon: <IconDelete className={styles.iconContextMenu} />,
                },
              ];

              const isInnerExpand = collapseItemId === menuItem.id;
              return (
                <div
                  className={cx(styles.collapseWrapper, {
                    [styles.expanded]: isInnerExpand,
                  })}
                  key={menuItem.id}
                >
                  <SidebarButton
                    className={cx(styles.button, styles.collapseButton)}
                    classes={{
                      startIcon: cx(styles.innerCollapseStartIcon, {
                        [styles.innerCollapseStartIconExpanded]: isInnerExpand,
                      }),
                    }}
                    item={menuItem}
                    onClick={() => handleCollapseItemClick(menuItem)}
                    startIcon={<menuItem.icon className={styles.expandCollapseIcon} />}
                  >
                    {menuItem.displayActionsMenu && (
                      <div className={styles.collapseButtonContent}>
                        <TruncatedTypography className={styles.collapseItemText}>
                          {menuItem.label}
                        </TruncatedTypography>
                        <ContextMenu
                          className={styles.collapseButtonActionMenu}
                          dataTestId={`collapseMenuItem${menuItem.id}`}
                          horizontal
                          small
                          items={contextMenuItems}
                        />
                      </div>
                    )}
                  </SidebarButton>
                  <Collapse
                    className={styles.innerCollapse}
                    in={isInnerExpand}
                  >
                    <div className={styles.innerCollapseContent}>
                      <div
                        className={cx(styles.verticalLine, {
                          [styles.isInnerCollapseExpand]: isInnerExpand,
                        })}
                      ></div>
                      {menuItem.items?.map((innerCollapseItem) => {
                        if (innerCollapseItem.disabled) {
                          return null;
                        }
                        return (
                          <SidebarSubmenuItem
                            className={cx(styles.item, styles.collapseInnerItem)}
                            classes={{
                              endIcon: styles.innerCollapseEndIcon,
                            }}
                            key={innerCollapseItem.id}
                            item={innerCollapseItem}
                            active={activePath.startsWith(innerCollapseItem.path)}
                          />
                        );
                      })}
                    </div>
                  </Collapse>
                </div>
              );
            }

            return (
              <SidebarSubmenuItem
                className={styles.item}
                key={menuItem.id}
                item={menuItem}
                active={activePath.startsWith(menuItem.path)}
              />
            );
          })}
        </div>
      </Collapse>
    </div>
  );
};

SidebarSubmenu.propTypes = {
  className: PropTypes.string,
  item: PropTypes.shape({}).isRequired,
  menuItemId: PropTypes.string,
  collapseItemId: PropTypes.string,
  onToggleMenu: PropTypes.func.isRequired,
  onToggleCollapse: PropTypes.func.isRequired,
  activePath: PropTypes.string,
};

SidebarSubmenu.defaultProps = {
  className: '',
  menuItemId: null,
  collapseItemId: null,
  activePath: '',
};
